import { DatePicker, Grid, Input, Tooltip } from '@design-systems/canvas-ui'
import Icon, { InfoIcon } from '@design-systems/react-icons'
import {
  FileDownloadOnly,
  FileHandler,
  formatCurrency,
  isOthersSelected,
  isPositiveDecimalNumber,
  isValidWholeNumber,
  isWithinPercentLimit,
} from 'mvscommoncomponents'
import { appContants } from '../../App'
import {
  buildDropdownOptions,
  exportMasterData,
  fetchLastXYearsDropdown,
  getControlError,
  getDependentDropdownOptions,
  getReactionGridDataField,
  getStrengthAssessmentByCapability,
  HeaderBanner,
  isControlError,
  isHierarchyDropdownEnabled,
  isOthersInArray,
  PackedReactionGrid,
} from '../../common'
import { AutocompleteInput } from '../../common/AutocompleteInput'
import PhoneNumerInput from '../../common/PhoneNumberInput'
import RadioButtonGroup from '../../common/RadioButtonGroup'
import ReactionGrid from '../../common/ReactionGrid'
import { TruncatedValueTooltip } from '../../common/TruncatedValueTooltip'
import {
  CONTROL_PHONE_NUMBER_FIELD_PHONE,
  DATA_TYPE_OBJECT,
  DIMENSIONS,
  FIELD_FABRIC_PRIMARY_TYPE,
  PLACEHOLDER_YYYY_MM_DD,
} from '../../common/commonConstants'
import {
  captureOthersValue,
  embedUnitDisplay,
  getControlWidth,
  getHeightClass,
  getUsNumberFormat,
  handleOtherValueDisplay,
  renderCaptureOthersValue,
  renderControlLabel,
  renderControlLabelSmallHeaderOnly,
  renderLabelReadOnlyHeader,
  showErrorMessage,
} from './controlHelper'

export const renderControlView = (
  control,
  sectionData,
  rfiMasterData,
  onInputChangeHandler,
  country,
) => {
  if (isBuildControl(control, sectionData, country)) {
    return renderControl(
      control,
      sectionData,
      rfiMasterData,
      onInputChangeHandler,
    )
  } else {
    return false
  }
}

/**
 * Is control to be hidden
 * @param {*} control
 * @param {*} sectionData
 * @returns
 */
export const isBuildControl = (control, sectionData, country) => {
  if (control.dependent && control.dependent.length > 0) {
    const dependentType = control.dependent_type || 'AND'

    if (dependentType.toUpperCase() === 'OR') {
      return evaluateOrLogic(control.dependent, sectionData, country)
    } else if (dependentType.toUpperCase() === 'NOT') {
      return evaluateNotLogic(control.dependent, sectionData, country)
    } else {
      return evaluateAndLogic(control.dependent, sectionData, country)
    }
  }

  return true
}

const evaluateNotLogic = (dependents, sectionData, country) => {
  for (let dependentControl of dependents) {
    let dependentFieldValue
    if (
      dependentControl.field === 'country_name' ||
      dependentControl.field === 'country'
    ) {
      dependentFieldValue = country
    } else {
      dependentFieldValue = sectionData[dependentControl.field]
    }
    const isBuildControl = !dependentControl.value.includes(dependentFieldValue)
    if (isBuildControl) {
      return true
    }
  }
  return false
}

const evaluateAndLogic = (dependents, sectionData, country) => {
  for (let dependentControl of dependents) {
    let dependentFieldValue
    if (
      dependentControl.field === 'country_name' ||
      dependentControl.field === 'country'
    ) {
      dependentFieldValue = country
    } else {
      dependentFieldValue = sectionData[dependentControl.field]
    }
    let isBuildControl = false
    if (Array.isArray(dependentFieldValue)) {
      isBuildControl = dependentFieldValue.some((val) =>
        dependentControl.value.includes(val),
      )
    } else {
      isBuildControl = dependentControl.value.includes(dependentFieldValue)
    }
    if (!isBuildControl) {
      return false
    }
  }
  return true
}

const getInputSelectValue = (idOrOption, value) => {
  if (value !== undefined) {
    return value?.value ?? value
  }

  return idOrOption?.value ?? idOrOption
}

const evaluateOrLogic = (dependents, sectionData, country) => {
  for (let dependentControl of dependents) {
    let dependentFieldValue
    if (
      dependentControl.field === 'country_name' ||
      dependentControl.field === 'country'
    ) {
      dependentFieldValue = country
    } else {
      dependentFieldValue = sectionData[dependentControl.field]
    }
    let isBuildControl = false
    if (Array.isArray(dependentFieldValue)) {
      isBuildControl = dependentFieldValue.some((val) =>
        dependentControl.value.includes(val),
      )
    } else {
      isBuildControl = dependentControl.value.includes(dependentFieldValue)
    }
    if (isBuildControl) {
      return true
    }
  }
  return false
}

/**
 * Build & return any control from passed control attribues
 * @param {*} control
 * @param {*} sectionData
 * @param {*} rfiMasterData
 * @param {*} onInputChangeHandler
 * @returns
 */
export const renderControl = (
  control, //Control template properties
  sectionData, //Entire section's data
  rfiMasterData, //Master data - to prepare the fixed options etc
  onInputChangeHandler, //Callback
) => {
  let controlPath = `${control.metadata?.sectionPath}.${control.uid}`
  let isControlErrored = isControlError(
    controlPath,
    control.metadata?.errorCache,
  )
  let otherControlPath = `${control.metadata?.sectionPath}.other_${control.uid}`
  let errorMessage = getControlError(controlPath, control.metadata?.errorCache)
  let otherErrorMessage = getControlError(
    otherControlPath,
    control.metadata?.errorCache,
  )
  switch (control.type.toUpperCase()) {
    case 'LABEL_READONLY_BANNER': {
      return (
        <HeaderBanner
          label_name={control.label_name}
          dataNode={sectionData[control.uid]}
          component_name={control.component_name}
        />
      )
    }
    case 'LABEL_READONLY_HEADER': {
      let valueToDisplay =
        control.metadata?.readonlyDisplayValue ??
        (sectionData
          ? sectionData[`other_${control.uid}`]
            ? `${sectionData[control.uid]} - ${
                sectionData[`other_${control.uid}`]
              }`
            : sectionData[control.uid]
          : '')

      return renderLabelReadOnlyHeader(
        valueToDisplay,
        control,
        sectionData,
        rfiMasterData,
      )
    }
    case 'LABEL_READONLY_NUMBER': {
      let valueToDisplay = getUsNumberFormat(sectionData[control.uid])
      return renderLabelReadOnlyHeader(
        valueToDisplay,
        control,
        sectionData,
        rfiMasterData,
      )
    }
    case 'CHIPS':
      return (
        <div className="width-100">
          <Input.Label
            className="hc8-fs-050 hc8-mb-none hc8-clr-placeholder"
            key={`label-header-${control.uid}`}
            id={`label-header-${control.uid}`}
          >
            <p
              className="hc8-clr-disabled"
              dangerouslySetInnerHTML={{
                __html: [control.label_name],
              }}
            ></p>
          </Input.Label>
          <div className="lcm-readonly-chip-list">
            {typeof sectionData[control.uid] === DATA_TYPE_OBJECT
              ? sectionData[control.uid]?.map((eachItem) => (
                  <div
                    className="lcm-readonly-chip-list__chip"
                    key={`${control.uid}-${eachItem}`}
                  >
                    <TruncatedValueTooltip
                      targetSelector=".lcm-readonly-chip-list__value"
                      value={handleOtherValueDisplay(
                        eachItem,
                        sectionData[`other_${control.uid}`],
                      )}
                    >
                      <Input.Chip
                        className="lcm-readonly-chip-list__value"
                        size="sm"
                        label={handleOtherValueDisplay(
                          eachItem,
                          sectionData[`other_${control.uid}`],
                        )}
                        isDisabled
                      />
                    </TruncatedValueTooltip>
                  </div>
                ))
              : sectionData[control.uid] && (
                  <div className="lcm-readonly-chip-list__chip">
                    <TruncatedValueTooltip
                      targetSelector=".lcm-readonly-chip-list__value"
                      value={handleOtherValueDisplay(
                        sectionData[control.uid],
                        sectionData[`other_${control.uid}`],
                      )}
                    >
                      <Input.Chip
                        className="lcm-readonly-chip-list__value"
                        size="sm"
                        label={handleOtherValueDisplay(
                          sectionData[control.uid],
                          sectionData[`other_${control.uid}`],
                        )}
                        isDisabled
                      />
                    </TruncatedValueTooltip>
                  </div>
                )}
          </div>
        </div>
      )
    case 'FACTORY_ADDRESS_READONLY': {
      let addressElements = []
      sectionData?.factory_address?.address?.forEach(
        (eachAddressElement, index) => {
          if (eachAddressElement.labels.length === 1) {
            addressElements.push(
              <Grid.Item size={{ xs: 3 }}>
                <Input.Label
                  className="hc8-fs-050 hc8-mb-none hc8-clr-disabled text-capitalize"
                  key={`factory-address-label-${eachAddressElement.labels[0]}`}
                  id={`factory-address-label-${eachAddressElement.labels[0]}`}
                >
                  {eachAddressElement.labels[0]}
                </Input.Label>
                <Input.Label
                  className="hc8-fs-100"
                  key={`factory-address-value-${eachAddressElement.values[0]}`}
                  id={`factory-address-value-${eachAddressElement.values[0]}`}
                >
                  {eachAddressElement.values[0]}
                </Input.Label>
              </Grid.Item>,
            )
          } else {
            let addressSubElements = []
            eachAddressElement.labels.forEach((eachElement, index) => {
              addressSubElements.push(
                <Grid.Item size={{ xs: 3 }}>
                  <Input.Label
                    className="hc8-fs-050 hc8-mb-none hc8-clr-disabled text-capitalize"
                    key={`factory-subaddress-label-${eachAddressElement.labels[index]}`}
                    id={`factory-subaddress-label-${eachAddressElement.labels[index]}`}
                  >
                    {eachAddressElement.labels[index]}
                  </Input.Label>
                  <Input.Label
                    className="hc8-fs-100"
                    key={`factory-subaddress-value-${eachAddressElement.values[index]}`}
                    id={`factory-subaddress-value-${eachAddressElement.values[index]}`}
                  >
                    {eachAddressElement.values[index]}
                  </Input.Label>
                </Grid.Item>,
              )
            })
            addressElements.push(<>{addressSubElements}</>)
          }
        },
      )
      return <Grid.Container>{addressElements}</Grid.Container>
    }
    case 'LABEL_READONLY':
      return (
        <div className="width-100">
          {renderControlLabel(control)}
          <Input.Label
            className="hc8-fs-050 font-weight-400"
            key={`label-readonly-${control.uid}`}
            id={`label-readonly-${control.uid}`}
          >
            <p
              dangerouslySetInnerHTML={{
                __html: [sectionData[control.uid]],
              }}
            ></p>
          </Input.Label>
        </div>
      )
    case 'CURRENCY_INTERNAL_READONLY':
      return (
        <div className="width-100">
          {renderControlLabelSmallHeaderOnly(control)}
          <Input.Label
            className="hc8-fs-100"
            key={`label-header-value-${control.uid}`}
            id={`label-header-value-${control.uid}`}
          >
            <p
              className="font-weight-400"
              dangerouslySetInnerHTML={{
                __html: [formatCurrency(sectionData[control.uid])],
              }}
            ></p>
          </Input.Label>
        </div>
      )
    case 'CURRENCY_READONLY':
      return (
        <div className="width-100">
          {renderControlLabel(control)}
          <Input.Label
            className="hc8-fs-100"
            key={`label-header-value-${control.uid}`}
            id={`label-header-value-${control.uid}`}
          >
            <p
              dangerouslySetInnerHTML={{
                __html: [formatCurrency(sectionData[control.uid])],
              }}
            ></p>
          </Input.Label>
        </div>
      )
    case 'CURRENCY':
      return (
        <div className="width-100">
          {renderControlLabel(control)}
          <div
            style={{ width: getControlWidth(control) }}
            className="margin-top-6"
          >
            <Input.Currency
              key={`currency-${control.uid}`}
              id={`currency-${control.uid}`}
              data-testid="currency-component"
              value={sectionData[control.uid] ? sectionData[control.uid] : ''}
              isErrored={isControlErrored}
              onChange={(id, value) => {
                let updateMetadata = {
                  uid: control.uid,
                  inputMetadata: control.metadata,
                }
                onInputChangeHandler(value, updateMetadata)
              }}
            />
            {showErrorMessage(errorMessage)}
          </div>
        </div>
      )
    case 'SECTION_INLINE_HEADER':
      return (
        <div className="width-100">
          <Input.Label
            className="hc8-fs-150 font-weight-500 hc8-mt-2x"
            key={`section-inline-header-${control.uid}`}
            id={`section-inline-header-${control.uid}`}
          >
            {control.label_name}
          </Input.Label>
          <div className="hc8-fs-100 hc8-pv-2x">{control.description}</div>
        </div>
      )
    case 'SUB_SECTION_INLINE_HEADER':
      return (
        <div className="width-100">
          <Input.Label
            className="hc8-fs-100 font-weight-500"
            key={`section-inline-header-${control.uid}`}
            id={`section-inline-header-${control.uid}`}
          >
            {control.label_name}
          </Input.Label>
        </div>
      )
    case 'DATEPICKER':
      return (
        <div className="width-100">
          {renderControlLabel(control)}
          <DatePicker
            key={`datepicker-${control.uid}`}
            id={`datepicker-${control.uid}`}
            data-testid="datepicker-component"
            className="hc8-mt-2x"
            format={PLACEHOLDER_YYYY_MM_DD}
            isErrored={isControlErrored}
            value={sectionData[control.uid]?.replaceAll('-', '/')}
            onChange={(id, value) => {
              let newDateValue = value?.replaceAll('/', '-')
              let updateMetadata = {
                uid: control.uid,
                inputMetadata: control.metadata,
              }
              onInputChangeHandler(newDateValue, updateMetadata)
            }}
            disableDates={{ future: true }}
            hintText={'Date cannot be in the future'}
          />
          <div className="hc8-pt-2x">{showErrorMessage(errorMessage)}</div>
        </div>
      )
    case 'AUTOCOMPLETE':
      return (
        <div className="width-100">
          {renderControlLabel(control)}
          <div style={{ width: getControlWidth(control) }}>
            <AutocompleteInput
              key={`autocomplete-input-${control.uid}`}
              id={`autocomplete-input-${control.uid}`}
              readOnly={control.metadata?.isreadOnly}
              value={
                control.metadata?.displayValue ??
                (sectionData[control.uid] ? sectionData[control.uid] : '')
              }
              class="font-size-16 height-32p"
              error={isControlErrored}
              options={
                control.metadata?.dropdownOptions
                  ? control.metadata.dropdownOptions
                  : buildDropdownOptions(
                      rfiMasterData,
                      {
                        nodeDotPath: control.options,
                        property: control.property,
                      },
                      true,
                      false,
                    )
              }
              disabled={control.metadata?.isDisabled}
              onUpdate={(value) => {
                let newValue = value?.value
                let updateMetadata = {
                  uid: control.uid,
                  inputMetadata: control.metadata,
                }
                onInputChangeHandler(
                  newValue,
                  updateMetadata,
                  control?.refresh_required,
                )
              }}
            />
            <div className="hc8-pt-2x">{showErrorMessage(errorMessage)}</div>
          </div>
        </div>
      )
    case 'AUTOCOMPLETE_OTHERS': {
      return (
        <div className="width-100">
          {renderControlLabel(control)}
          <div style={{ width: getControlWidth(control) }}>
            <>
              <AutocompleteInput
                key={`autocomplete-input-main-${control.uid}`}
                id={`autocomplete-input-main-${control.uid}`}
                value={
                  control.metadata?.displayValue ??
                  (sectionData[control.uid] ? sectionData[control.uid] : '')
                }
                class="font-size-16 height-32p"
                error={isControlErrored}
                className="hc8-mb-none"
                options={
                  control.metadata?.dropdownOptions
                    ? control.metadata.dropdownOptions
                    : buildDropdownOptions(
                        rfiMasterData,
                        {
                          nodeDotPath: control.options,
                          property: control.property,
                        },
                        true,
                        false,
                      )
                }
                onUpdate={(value) => {
                  let newValue = value?.value
                  let updateMetadata = {
                    uid: control.uid,
                    inputMetadata: control.metadata,
                  }
                  onInputChangeHandler(
                    newValue,
                    updateMetadata,
                    control?.refresh_required,
                  )
                }}
              />
              {errorMessage && (
                <div className="hc8-pt-2x">
                  {showErrorMessage(errorMessage)}
                </div>
              )}
            </>
            {captureOthersValue(
              control,
              sectionData,
              onInputChangeHandler,
              otherErrorMessage,
            )}
          </div>
        </div>
      )
    }
    case 'AUTOCOMPLETE_HIERARCHY': {
      return (
        <div className="width-100">
          {renderControlLabel(control)}
          <div style={{ width: getControlWidth(control) }}>
            <AutocompleteInput
              key={`autocomplete-hierarchy-input-${control.uid}`}
              id={`autocomplete-hierarchy-input-${control.uid}`}
              readOnly={control.metadata?.isreadOnly}
              value={sectionData[control.uid] ? sectionData[control.uid] : ''}
              class="font-size-16 height-32p"
              error={isControlErrored}
              options={getDependentDropdownOptions(
                rfiMasterData,
                sectionData,
                control,
              )}
              disabled={!isHierarchyDropdownEnabled(control, sectionData)}
              onUpdate={(value) => {
                let newValue = value?.value
                let updateMetadata = {
                  uid: control.uid,
                  inputMetadata: control.metadata,
                  type: control.type,
                }
                onInputChangeHandler(
                  newValue,
                  updateMetadata,
                  control?.refresh_required,
                )
              }}
            />
            <div className="hc8-pt-2x">{showErrorMessage(errorMessage)}</div>
            {captureOthersValue(
              control,
              sectionData,
              onInputChangeHandler,
              otherErrorMessage,
            )}
          </div>
        </div>
      )
    }
    case 'MULTI_AUTOCOMPLETE_HIERARCHY': {
      return (
        <div className="width-100">
          {renderControlLabel(control)}
          <div style={{ width: getControlWidth(control) }}>
            <AutocompleteInput
              key={`autocomplete-hierarchy-input-${control.uid}`}
              id={`autocomplete-hierarchy-input-${control.uid}`}
              data-testid="multi-autocomplete-hierarchy-component"
              readOnly={control.metadata?.isreadOnly}
              value={buildDropdownOptions(
                sectionData[control.uid] ? sectionData[control.uid] : [],
                null,
                false,
                false,
              )}
              class="font-size-16"
              error={isControlErrored}
              multiselect={true}
              options={getDependentDropdownOptions(
                rfiMasterData,
                sectionData,
                control,
              )}
              disabled={!isHierarchyDropdownEnabled(control, sectionData)}
              onUpdate={(value) => {
                let newList = value.map((eachValue) => {
                  return eachValue.value
                })
                let updateMetadata = {
                  uid: control.uid,
                  inputMetadata: control.metadata,
                  type: control.type,
                }
                onInputChangeHandler(
                  newList,
                  updateMetadata,
                  control?.refresh_required,
                )
              }}
            />
            {sectionData[control.uid] &&
              isOthersInArray(sectionData[control.uid]) &&
              renderCaptureOthersValue(
                control,
                sectionData,
                onInputChangeHandler,
                otherErrorMessage,
              )}
            <div className="hc8-pt-2x">{showErrorMessage(errorMessage)}</div>
          </div>
        </div>
      )
    }
    case 'LABEL_READONLY_HEADER_OTHERS': {
      const customReadonlyValue = control.metadata?.readonlyDisplayValue
      const hasCustomReadonlyValue =
        customReadonlyValue !== undefined &&
        customReadonlyValue !== null &&
        customReadonlyValue !== ''
      const valueToDisplay = hasCustomReadonlyValue
        ? customReadonlyValue
        : sectionData[control.uid]

      return (
        <div className="width-100">
          <Input.Label
            className="hc8-fs-050 hc8-mb-none hc8-clr-placeholder"
            key={`label-header-${control.uid}`}
            id={`label-header-${control.uid}`}
          >
            <p
              className="hc8-clr-disabled"
              dangerouslySetInnerHTML={{
                __html: [control.label_name],
              }}
            ></p>
          </Input.Label>
          <Input.Label
            className="hc8-fs-100 hc8-mt-2x"
            key={`label-header-value-${control.uid}`}
            id={`label-header-value-${control.uid}`}
          >
            <p
              className="font-weight-400"
              dangerouslySetInnerHTML={{
                __html: [valueToDisplay],
              }}
            ></p>
          </Input.Label>

          {!hasCustomReadonlyValue &&
            isOthersSelected(sectionData[control.uid]) && (
              <Input.Label
                key={`autocomplete-input-other-${control.uid}`}
                id={`autocomplete-input-other-${control.uid}`}
                className="hc8-fs-100"
              >
                <p
                  className="font-weight-400"
                  dangerouslySetInnerHTML={{
                    __html: [sectionData[`other_${control.uid}`]],
                  }}
                ></p>
              </Input.Label>
            )}
        </div>
      )
    }
    case 'MULTI_AUTOCOMPLETE': {
      return (
        <div className="width-100">
          {renderControlLabel(control)}
          <div style={{ width: getControlWidth(control) }}>
            <AutocompleteInput
              key={`autocomplete-input-multi-${control.uid}`}
              id={`autocomplete-input-multi-${control.uid}`}
              disabled={control.metadata?.isDisabled}
              readOnly={control.metadata?.isreadOnly}
              value={buildDropdownOptions(
                sectionData[control.uid] ? sectionData[control.uid] : [],
                null,
                false,
                false,
              )}
              class="font-size-16"
              multiselect={true}
              defaultPlaceholder={control.placeholder}
              error={isControlErrored}
              options={
                control.metadata?.dropdownOptions
                  ? control.metadata.dropdownOptions
                  : buildDropdownOptions(
                      rfiMasterData,
                      {
                        nodeDotPath: control.options,
                        property: control.property,
                      },
                      true,
                      false,
                    )
              }
              onUpdate={(value) => {
                let newList = value.map((eachValue) => {
                  return eachValue.value
                })
                let updateMetadata = {
                  uid: control.uid,
                  inputMetadata: control.metadata,
                }
                onInputChangeHandler(
                  newList,
                  updateMetadata,
                  control?.refresh_required,
                )
              }}
            />
          </div>
          {showErrorMessage(errorMessage)}
        </div>
      )
    }
    case 'MULTI_AUTOCOMPLETE_OTHERS': {
      return (
        <div className="width-100">
          {renderControlLabel(control)}
          <div style={{ width: getControlWidth(control) }}>
            <AutocompleteInput
              key={`autocomplete-input-multi-${control.uid}`}
              id={`autocomplete-input-multi-${control.uid}`}
              value={buildDropdownOptions(
                sectionData[control.uid] ? sectionData[control.uid] : [],
                null,
                false,
                false,
              )}
              class="font-size-16"
              multiselect={true}
              defaultPlaceholder={control.placeholder}
              error={isControlErrored}
              options={
                control.metadata?.dropdownOptions
                  ? control.metadata.dropdownOptions
                  : buildDropdownOptions(
                      rfiMasterData,
                      {
                        nodeDotPath: control.options,
                        property: control.property,
                      },
                      true,
                      false,
                    )
              }
              onUpdate={(value) => {
                let newList = value.map((eachValue) => {
                  return eachValue.value
                })
                let updateMetadata = {
                  uid: control.uid,
                  inputMetadata: control.metadata,
                }
                onInputChangeHandler(
                  newList,
                  updateMetadata,
                  control?.refresh_required,
                )
              }}
            />
            {sectionData[control.uid] &&
              isOthersInArray(sectionData[control.uid]) &&
              renderCaptureOthersValue(
                control,
                sectionData,
                onInputChangeHandler,
                otherErrorMessage,
              )}
          </div>
        </div>
      )
    }
    case 'MULTI_AUTOCOMPLETE_READONLY': {
      return (
        <div className="width-100">
          {renderControlLabelSmallHeaderOnly(control)}
          <div style={{ width: getControlWidth(control) }}>
            <AutocompleteInput
              key={`autocomplete-input-multi-${control.uid}`}
              id={`autocomplete-input-multi-${control.uid}`}
              data-testid="multi-autocomplete-component"
              readOnly={true}
              value={buildDropdownOptions(
                sectionData[control.uid] ? sectionData[control.uid] : [],
                null,
                false,
                false,
              )}
              class="font-size-16"
              multiselect={true}
              options={[]}
              onUpdate={() => {}}
            />
          </div>
          {showErrorMessage(errorMessage)}
        </div>
      )
    }
    case 'DROPDOWN':
      return (
        <div className="width-100">
          {renderControlLabel(control)}
          <Input.Select
            key={`dropdown-${control.uid}`}
            id={`dropdown-${control.uid}`}
            data-testid="dropdown-component"
            className="hc8-mt-1x"
            isErrored={isControlErrored}
            style={{ width: getControlWidth(control) }}
            options={buildDropdownOptions(
              rfiMasterData,
              {
                nodeDotPath: control.options,
                property: control.property,
              },
              true,
              true,
            )}
            value={sectionData[control.uid] ? sectionData[control.uid] : ''}
            onChange={(idOrOption, value) => {
              const selectedValue = getInputSelectValue(idOrOption, value)
              if (selectedValue) {
                let updateMetadata = {
                  uid: control.uid,
                  inputMetadata: control.metadata,
                }
                onInputChangeHandler(selectedValue, updateMetadata)
              }
            }}
          />
          <div className="hc8-pt-2x">{showErrorMessage(errorMessage)}</div>
        </div>
      )
    case 'DROPDOWN_OTHERS':
      return (
        <div className="width-100">
          {renderControlLabel(control)}
          <>
            <Input.Select
              key={`dropdown-other-main-${control.uid}`}
              id={`dropdown-other-main-${control.uid}`}
              data-testid="drdopdown_others"
              className="hc8-mt-1x"
              isErrored={isControlErrored}
              style={{ width: getControlWidth(control) }}
              options={buildDropdownOptions(
                rfiMasterData,
                {
                  nodeDotPath: control.options,
                  property: control.property,
                },
                true,
                true,
              )}
              value={sectionData[control.uid] ? sectionData[control.uid] : ''}
              onChange={(idOrOption, value) => {
                const selectedValue = getInputSelectValue(idOrOption, value)
                if (selectedValue) {
                  let updateMetadata = {
                    uid: control.uid,
                    inputMetadata: control.metadata,
                  }
                  onInputChangeHandler(selectedValue, updateMetadata)
                }
              }}
            />
            {showErrorMessage(errorMessage)}
          </>
          {captureOthersValue(
            control,
            sectionData,
            onInputChangeHandler,
            otherErrorMessage,
          )}
        </div>
      )
    case 'TEXTAREA':
      return (
        <>
          {renderControlLabel(control)}
          <Input.Textarea
            key={`textarea-${control.uid}`}
            id={`textarea-${control.uid}`}
            className="hc8-mt-2x"
            placeholder={control.placeholder}
            value={sectionData[control.uid]}
            isErrored={isControlErrored}
            onChange={(event) => {
              let newValue = event.target.value
              let updateMetadata = {
                uid: control.uid,
                inputMetadata: control.metadata,
              }
              onInputChangeHandler(newValue, updateMetadata)
            }}
          />
          {showErrorMessage(errorMessage)}
        </>
      )
    case 'TEXTBOX': {
      const isDisabled = !!control?.metadata?.isDisabled
      const isReadonly = !!control?.metadata?.readonly

      return (
        <>
          {renderControlLabel(control)}
          <Input.Text
            key={`textbox-${control.uid}`}
            id={`textbox-${control.uid}`}
            data-testid="textbox-component"
            style={{ width: getControlWidth(control) }}
            className="hc8-mt-1x"
            value={sectionData[control.uid]}
            isErrored={isControlErrored}
            disabled={isDisabled}
            readOnly={isReadonly}
            onChange={(event) => {
              if (isDisabled || isReadonly) return
              let newValue = event.target.value
              let updateMetadata = {
                uid: control.uid,
                inputMetadata: control.metadata,
              }
              onInputChangeHandler(newValue, updateMetadata)
            }}
          />
          <div className="hc8-pt-2x">{showErrorMessage(errorMessage)}</div>
        </>
      )
    }
    case 'PERCENTAGE_BOX': {
      const isDisabled = !!control?.metadata?.isDisabled
      const isReadonly = !!control?.metadata?.readonly

      return (
        <>
          {renderControlLabel(control)}
          <Input.Text
            key={`textbox-perc-${control.uid}`}
            id={`textbox-perc-${control.uid}`}
            data-testid="percentage-box-component"
            className="hc8-mt-1x"
            style={{ width: getControlWidth(control) }}
            value={sectionData[control.uid]}
            isErrored={isControlErrored}
            isDisabled={isDisabled}
            isReadOnly={isReadonly}
            onChange={(event) => {
              if (isDisabled || isReadonly) return
              let newValue = event.target.value
              var isValidNumber = isWithinPercentLimit(newValue)
              let updateMetadata = {
                uid: control.uid,
                inputMetadata: control.metadata,
              }
              newValue =
                newValue === '' || isValidNumber
                  ? newValue
                  : sectionData[control.uid]
                    ? sectionData[control.uid]
                    : ''
              onInputChangeHandler(newValue, updateMetadata)
            }}
          />{' '}
          %<div className="hc8-pt-2x">{showErrorMessage(errorMessage)}</div>
        </>
      )
    }

    case 'PERCENTAGE_BOX_READONLY': {
      let valueToDisplay =
        sectionData && sectionData[control.uid]
          ? `${sectionData[control.uid]}%`
          : ''
      return renderLabelReadOnlyHeader(
        valueToDisplay,
        control,
        sectionData,
        rfiMasterData,
      )
    }
    case 'NUMBER_BOX':
      return (
        <>
          {renderControlLabel(control)}
          <div className="align-centre-left">
            <Input.Text
              key={`textbox-perc-number-${control.uid}`}
              id={`textbox-perc-number-${control.uid}`}
              data-testid="number-box-component"
              className="hc8-mt-1x"
              style={{ width: getControlWidth(control) }}
              value={sectionData[control.uid]}
              isErrored={isControlErrored}
              onChange={(event) => {
                let newValue = event.target.value

                var isValidNumber = control.decimals
                  ? isPositiveDecimalNumber(newValue, control.decimals)
                  : isValidWholeNumber(newValue)

                let updateMetadata = {
                  uid: control.uid,
                  inputMetadata: control.metadata,
                }
                newValue =
                  newValue === '' || isValidNumber
                    ? newValue
                    : sectionData[control.uid]
                      ? sectionData[control.uid]
                      : ''
                onInputChangeHandler(newValue, updateMetadata)
              }}
            />
            <p>{embedUnitDisplay(control, sectionData, rfiMasterData)}</p>
          </div>
          <div className="hc8-pt-2x">{showErrorMessage(errorMessage)}</div>
        </>
      )
    case 'DIMENSIONS':
      return (
        <>
          {renderControlLabel(control)}
          <div className="align-centre-left">
            <Input.Text
              key={`textbox-length-number-${control.uid + '_length'}`}
              id={`textbox-length-number-${control.uid + '_length'}`}
              className="hc8-mt-1x"
              data-testid="dimensions-component"
              style={{ width: getControlWidth(control) }}
              value={
                sectionData[control.uid] != null &&
                sectionData[control.uid][control.uid + '_length'] != null
                  ? sectionData[control.uid][control.uid + '_length']
                  : ''
              }
              placeholder="Length"
              isErrored={isControlErrored}
              onChange={(event) => {
                let newValue = event.target.value

                var decimals = control.controls.find(
                  (x) => x.uid === control.uid + '_length',
                ).decimals

                var isValidNumber = decimals
                  ? isPositiveDecimalNumber(newValue, decimals)
                  : isValidWholeNumber(newValue)

                let updateMetadata = {
                  uid: control.uid + '_length',
                  inputMetadata: control.metadata,
                }
                newValue =
                  newValue === '' || isValidNumber
                    ? newValue
                    : sectionData[control.uid][control.uid + '_length']
                      ? sectionData[control.uid][control.uid + '_length']
                      : ''
                onInputChangeHandler(
                  newValue,
                  updateMetadata,
                  DIMENSIONS,
                  control.uid,
                )
              }}
            />
            <span className="hc8-mh-2x">X</span>
            <Input.Text
              key={`textbox-length-number-${control.uid + '_width'}`}
              id={`textbox-length-number-${control.uid + '_width'}`}
              className="hc8-mt-1x"
              style={{ width: getControlWidth(control) }}
              value={
                sectionData[control.uid] != null &&
                sectionData[control.uid][control.uid + '_width'] != null
                  ? sectionData[control.uid][control.uid + '_width']
                  : ''
              }
              placeholder="Width"
              isErrored={isControlErrored}
              onChange={(event) => {
                let newValue = event.target.value

                var decimals = control.controls.find(
                  (x) => x.uid === control.uid + '_width',
                ).decimals

                var isValidNumber = decimals
                  ? isPositiveDecimalNumber(newValue, decimals)
                  : isValidWholeNumber(newValue)

                let updateMetadata = {
                  uid: control.uid + '_width',
                  inputMetadata: control.metadata,
                }
                newValue =
                  newValue === '' || isValidNumber
                    ? newValue
                    : sectionData[control.uid][control.uid + '_width']
                      ? sectionData[control.uid][control.uid + '_width']
                      : ''
                onInputChangeHandler(
                  newValue,
                  updateMetadata,
                  DIMENSIONS,
                  control.uid,
                )
              }}
            />
          </div>
          <div className="hc8-pt-2x">{showErrorMessage(errorMessage)}</div>
        </>
      )
    case 'DIMENSIONS_READONLY': {
      let length = !sectionData[control.uid]?.[control.uid + '_length']
        ? ''
        : sectionData[control.uid][control.uid + '_length']
      let breadth = !sectionData[control.uid]?.[control.uid + '_width']
        ? ''
        : sectionData[control.uid][control.uid + '_width']

      return renderLabelReadOnlyHeader(
        length || breadth ? `${length} X ${breadth}` : '',
        control,
        sectionData,
        rfiMasterData,
      )
    }
    case 'RADIO_BUTTON_GROUP':
      return (
        <div
          className={
            isControlErrored ? 'radio-button-group-error hc8-pa-2x' : ''
          }
        >
          {renderControlLabel(control)}
          <RadioButtonGroup
            key={`rbg-${control.uid}`}
            id={`rbg-${control.uid}`}
            value={sectionData?.[control.uid]}
            data-testid="radio-buttongroup-component"
            options={exportMasterData(rfiMasterData, {
              nodeDotPath: control.options,
            })}
            onOptionSelect={(value) => {
              let updateMetadata = {
                uid: control.uid,
                inputMetadata: control.metadata,
              }
              onInputChangeHandler(value, updateMetadata)
            }}
          />
          {showErrorMessage(errorMessage)}
        </div>
      )
    case 'PHONE_NUMBER_INPUT':
      return (
        <>
          {renderControlLabel(control)}
          <div className="hc8-mt-1x">
            <PhoneNumerInput
              key={`phone-${control.uid}`}
              id={`phone-${control.uid}`}
              labelName={control?.label_name}
              infoIconMessage={control?.info_icon}
              options={buildDropdownOptions(
                rfiMasterData,
                {
                  nodeDotPath: control.options,
                  property: control.property,
                },
                true,
                false,
              )}
              countryCode={
                sectionData[
                  `${
                    !control.number_type ||
                    control.number_type.toLowerCase() ===
                      CONTROL_PHONE_NUMBER_FIELD_PHONE
                      ? ''
                      : control.number_type + '_'
                  }country_code`
                ]
              }
              phoneNumber={
                sectionData[
                  `${
                    !control.number_type ||
                    control.number_type.toLowerCase() ===
                      CONTROL_PHONE_NUMBER_FIELD_PHONE
                      ? ''
                      : control.number_type + '_'
                  }phone_number`
                ]
              }
              updateCountryPhoneNumber={(newCountryCode, newPhoneNumber) => {
                let updateMetadata = {
                  uid: control.uid,
                  inputMetadata: control.metadata,
                  type: control.type,
                  numberType: control.number_type,
                }
                let newValue = {
                  countryCode: newCountryCode,
                  phoneNumber: newPhoneNumber,
                }
                onInputChangeHandler(newValue, updateMetadata)
              }}
            />
            {showErrorMessage(errorMessage)}
          </div>
        </>
      )
    case 'COLUMN_SPACE':
      return <div data-testid="column-space"></div>
    case 'LINE_SPACE':
      return <div className={getHeightClass(control.height)}></div>
    case 'REACTION_GRID':
      return (
        <>
          <ReactionGrid
            isreadOnly={control.metadata?.isreadOnly}
            key={`rg-${control.uid}`}
            id={`rg-${control.uid}`}
            info={control.info}
            heading={getReactionGridDataField(
              rfiMasterData[control.options],
              sectionData,
              control.uid,
              control.uid,
              'display_name',
            )}
            fields={getReactionGridDataField(
              rfiMasterData[control.options],
              sectionData,
              control.uid,
              control.uid,
              'rows',
            )}
            options={getReactionGridDataField(
              rfiMasterData[control.options],
              sectionData,
              control.uid,
              control.uid,
              'title',
            )}
            value={sectionData?.capabilities}
            section={control.uid}
            groupName={control.uid}
            updateGridValues={(
              groupName,
              displayName,
              section,
              valueMatrix,
            ) => {
              if (valueMatrix) {
                let updateMetadata = {
                  groupName: groupName,
                  inputMetadata: control.metadata,
                  displayName: displayName,
                  section: section,
                }
                onInputChangeHandler(valueMatrix, updateMetadata)
              }
            }}
          />
          {showErrorMessage(errorMessage)}
        </>
      )
    case 'STRENGTH_ASSESSMENT': {
      let masterDataSubset = exportMasterData(rfiMasterData, {
        nodeDotPath: control.options,
      })
      let segments = getStrengthAssessmentByCapability(
        masterDataSubset,
        sectionData[FIELD_FABRIC_PRIMARY_TYPE],
      )
      let pkdRgTemplate = { ...masterDataSubset, segments }
      delete pkdRgTemplate.capability_segments

      return pkdRgTemplate?.segments ? (
        <>
          <PackedReactionGrid
            isReadOnly={control.metadata?.isreadOnly}
            label={control.label_name}
            value={
              sectionData[control.uid]
                ? sectionData[control.uid]
                : { segments: [] }
            }
            template={pkdRgTemplate}
            rowIndex={control.metadata?.index}
            controlPath={controlPath}
            errorCache={control.metadata?.errorCache}
            onGridUpdate={(updatedGridValue) => {
              let updateMetadata = {
                uid: control.uid,
                inputMetadata: control.metadata,
              }
              onInputChangeHandler(updatedGridValue, updateMetadata)
            }}
          />
          {showErrorMessage(errorMessage)}
        </>
      ) : (
        <></>
      )
    }
    case 'YEAR_DROPDOWN':
      return (
        <div className="width-100">
          {renderControlLabel(control)}
          <Input.Select
            key={`dropdown-${control.uid}`}
            id={`dropdown-${control.uid}`}
            data-testid="dropdown-test_uid"
            className="hc8-mt-2x"
            isErrored={isControlErrored}
            style={{ width: getControlWidth(control) }}
            options={fetchLastXYearsDropdown(control.options)}
            value={sectionData[control.uid] ? sectionData[control.uid] : ''}
            onChange={(idOrOption, value) => {
              const selectedValue = getInputSelectValue(idOrOption, value)
              if (selectedValue) {
                let updateMetadata = {
                  uid: control.uid,
                  inputMetadata: control.metadata,
                }
                onInputChangeHandler(selectedValue, updateMetadata)
              }
            }}
          />
          <div className="hc8-pt-2x">{showErrorMessage(errorMessage)}</div>
        </div>
      )
    case 'FILEUPLOAD': {
      return (
        <div className="width-100">
          {renderControlLabel(control)}
          <div className="hc8-pt-6x">
            <FileHandler
              data-testid="fileupload-component"
              fileUploadConstants={appContants?.rfiFileUploadConstants}
              isFileActionInProgress={control.metadata?.isFileActionInProgress}
              isFileActionFailed={control.metadata?.isFileActionFailed}
              fileHandlerData={
                sectionData[control.uid] ? sectionData[control.uid] : {}
              }
              uploadFiles={(files) => {
                return control.metadata?.uploadFile(
                  files,
                  control.uid,
                  control.component_name,
                  control.metadata,
                )
              }}
              downloadFiles={(fileName, tossKey) => {
                return control.metadata?.downloadFile(
                  fileName,
                  tossKey,
                  control.component_name,
                  control.metadata,
                )
              }}
              deleteFiles={(fileName) => {
                control.metadata?.deleteFile(
                  fileName,
                  control.uid,
                  control.component_name,
                  control.metadata,
                )
              }}
            />
          </div>
        </div>
      )
    }
    case 'PHONE_NUMBER_INPUT_READONLY': {
      let countryCode =
        sectionData[
          `${
            !control.number_type ||
            control.number_type.toLowerCase() === 'phone'
              ? ''
              : control.number_type + '_'
          }country_code`
        ]
      let phoneNumber =
        sectionData[
          `${
            !control.number_type ||
            control.number_type.toLowerCase() === 'phone'
              ? ''
              : control.number_type + '_'
          }phone_number`
        ]
      return (
        <div className="width-100">
          <Input.Label
            className="hc8-fs-050 hc8-mb-none hc8-clr-placeholder"
            key={`label-header-${control.uid}`}
            id={`label-header-${control.uid}`}
          >
            <p
              className="hc8-clr-disabled"
              dangerouslySetInnerHTML={{
                __html: [control.label_name],
              }}
            ></p>
          </Input.Label>
          <Input.Label
            className="hc8-fs-100 hc8-mt-2x"
            key={`label-header-value-${control.uid}`}
            id={`label-header-value-${control.uid}`}
          >
            <p
              className="font-weight-400"
              dangerouslySetInnerHTML={{
                __html: [
                  (countryCode ? countryCode : '') +
                    ' ' +
                    (phoneNumber ? phoneNumber : ''),
                ],
              }}
            ></p>
          </Input.Label>
        </div>
      )
    }
    case 'FILEDOWNLOAD': {
      return (
        <div className="width-100">
          {control.label_name &&
            (control.uid === 'uploaded_files' ? (
              <Input.Label className="hc8-fs-150 font-weight-500 hc8-mt-2x">
                {control.label_name}
              </Input.Label>
            ) : (
              renderControlLabelSmallHeaderOnly(
                control.label_name,
                control.info_icon,
              )
            ))}
          <div className="hc8-pb-3x">
            <FileDownloadOnly
              fileHandlerData={
                control.uid === 'uploaded_files'
                  ? sectionData[control.uid]
                    ? { uploadedFiles: sectionData[control.uid] }
                    : {}
                  : sectionData[control.uid]
                    ? { uploadedFiles: sectionData[control.uid].uploaded_files }
                    : {}
              }
              downloadFiles={(fileName, tossKey) => {
                return control.metadata?.downloadFile(
                  fileName,
                  tossKey,
                  control.component_name,
                )
              }}
            />
          </div>
        </div>
      )
    }
    case 'INFO_BANNER':
      return (
        <div className="warning-color-background hc8-pt-6x hc8-pl-4x">
          <span>
            {control.info_icon ? (
              <Tooltip location="top" content={control.info_icon}>
                <Icon svg={InfoIcon} />
              </Tooltip>
            ) : (
              <Icon svg={InfoIcon} />
            )}
          </span>
          <span className="hc8-pl-2x">{control.label_name}</span>
        </div>
      )
    case 'NUMBER_UNIT': {
      const basePath = control?.metadata?.sectionPath
      const errCache = control?.metadata?.errorCache

      const numChild = control?.controls?.[0]
      const unitChild = control?.controls?.[1]

      const numberPath =
        numChild?.uid && basePath ? `${basePath}.${numChild.uid}` : undefined
      const unitPath =
        unitChild?.uid && basePath ? `${basePath}.${unitChild.uid}` : undefined

      const numberHasError = Boolean(
        numberPath && errCache?.controls && numberPath in errCache.controls,
      )
      const unitHasError = Boolean(
        unitPath && errCache?.controls && unitPath in errCache.controls,
      )

      const errorMessage =
        (numberHasError && errCache.controls[numberPath]) ||
        (unitHasError && errCache.controls[unitPath]) ||
        null

      return (
        <>
          {renderControlLabel(control)}

          <Grid.Container
            spacing="dense"
            align="center"
            className="hc8-mt-none"
          >
            {/* ➕ Numeric field */}
            <Grid.Item size={{ xs: 6 }}>
              <Input.Text
                key={`textbox-perc-number-${numChild?.uid}`}
                id={`textbox-perc-number-${numChild?.uid}`}
                data-testid="number-box-component"
                className="hc8-mb-none"
                value={sectionData[numChild?.uid] ?? ''}
                isErrored={numberHasError}
                isDisabled={numChild?.metadata?.isDisabled}
                onChange={(event) => {
                  const newValue = event.target.value
                  const child = numChild

                  const isValidNumber = child?.decimals
                    ? isPositiveDecimalNumber(newValue, child.decimals)
                    : isValidWholeNumber(newValue)

                  const updateMetadata = {
                    uid: child.uid,
                    inputMetadata: {
                      ...control,
                      subControl: child.uid,
                    },
                  }

                  const finalValue =
                    newValue === '' || isValidNumber
                      ? newValue
                      : (sectionData[child.uid] ?? '')

                  onInputChangeHandler(finalValue, updateMetadata)
                }}
              />
            </Grid.Item>

            {/* ➕ Unit (autocomplete OR textbox) */}
            <Grid.Item className="hc8-pa-none" size={{ xs: 5 }}>
              <div className="central-control-unit-padding">
                {unitChild?.type === 'textbox' ? (
                  <Input.Text
                    key={`textbox-${unitChild?.uid}`}
                    id={`textbox-${unitChild?.uid}`}
                    data-testid="textbox-component"
                    className="hc8-mb-none"
                    value={sectionData[unitChild?.uid] ?? ''}
                    isErrored={unitHasError}
                    isDisabled={unitChild?.metadata?.isDisabled}
                    onChange={(event) => {
                      const newValue = event.target.value
                      const child = unitChild
                      const updateMetadata = {
                        uid: child.uid,
                        inputMetadata: {
                          ...control,
                          subControl: child.uid,
                        },
                      }
                      onInputChangeHandler(newValue, updateMetadata)
                    }}
                  />
                ) : (
                  <AutocompleteInput
                    key={`autocomplete-input-${unitChild?.uid}`}
                    id={`autocomplete-input-${unitChild?.uid}`}
                    readOnly={control.metadata?.isreadOnly}
                    value={sectionData[unitChild?.uid] ?? ''}
                    error={unitHasError}
                    class="font-size-16 height-30p hc8-mb-none"
                    disabled={unitChild?.metadata?.isDisabled}
                    options={
                      control.metadata?.dropdownOptions
                        ? control.metadata.dropdownOptions
                        : buildDropdownOptions(
                            rfiMasterData,
                            {
                              nodeDotPath: unitChild?.options,
                              property: unitChild?.property,
                            },
                            true,
                            false,
                          )
                    }
                    onUpdate={(value) => {
                      const child = unitChild
                      const updateMetadata = {
                        uid: child.uid,
                        inputMetadata: {
                          ...control,
                          subControl: child.uid,
                        },
                      }
                      const newValue = value?.value
                      onInputChangeHandler(newValue, updateMetadata)
                    }}
                  />
                )}
              </div>
            </Grid.Item>
          </Grid.Container>

          {/* Shared error below both controls */}
          {errorMessage ? (
            <div className="hc8-pt-2x">{showErrorMessage(errorMessage)}</div>
          ) : null}
        </>
      )
    }

    case 'NUMBER_UNIT_READONLY': {
      const numberControl = control.controls?.[0]
      const unitControl = control.controls?.[1]

      const numberValue = numberControl
        ? getUsNumberFormat(sectionData?.[numberControl.uid])
        : ''

      const unitValue = unitControl ? sectionData?.[unitControl.uid] : ''

      const labelText = unitValue
        ? `${numberValue} ${unitValue}`
        : `${numberValue}`

      return renderLabelReadOnlyHeader(
        labelText,
        control,
        sectionData,
        rfiMasterData,
      )
    }

    case 'CHECKBOX_GROUP': {
      if (control?.metadata?.readonly) {
        const values = Array.isArray(sectionData?.[control.uid])
          ? sectionData[control.uid]
          : []
        const valueToDisplay = values.length ? values.join(', ') : ''
        return renderLabelReadOnlyHeader(
          valueToDisplay,
          control,
          sectionData,
          rfiMasterData,
        )
      }

      const selected = Array.isArray(sectionData[control.uid])
        ? sectionData[control.uid]
        : []

      const options = control.metadata?.dropdownOptions
        ? control.metadata.dropdownOptions
        : buildDropdownOptions(
            rfiMasterData,
            { nodeDotPath: control.options, property: control.property },
            true,
            false,
          )

      const controlPath = `${control.metadata?.sectionPath}.${control.uid}`
      const isErrored = isControlError(
        controlPath,
        control.metadata?.errorCache,
      )
      const errorMessage = getControlError(
        controlPath,
        control.metadata?.errorCache,
      )

      const toggle = (value) => {
        const exists = selected.includes(value)
        const next = exists
          ? selected.filter((v) => v !== value)
          : [...selected, value]
        const updateMetadata = {
          uid: control.uid,
          inputMetadata: control.metadata,
        }
        onInputChangeHandler(next, updateMetadata)
      }

      return (
        <div className={isErrored ? 'checkbox-group-error hc8-pa-2x' : ''}>
          {renderControlLabel(control)}
          <div>
            {options?.map((opt) => (
              <div key={`${control.uid}-${opt.value}`}>
                <Input.Checkbox
                  id={`${control.uid}-${opt.value}`}
                  isChecked={selected.includes(opt.value)}
                  isDisabled={control.metadata?.isDisabled}
                  label={opt.label}
                  onChange={() => toggle(opt.value)}
                />
              </div>
            ))}
            <div className="hc8-pt-2x">{showErrorMessage(errorMessage)}</div>
          </div>
        </div>
      )
    }

    case 'CHECKBOX_GROUP_READONLY': {
      const values = Array.isArray(sectionData?.[control.uid])
        ? sectionData[control.uid]
        : []
      const valueToDisplay = values.length ? values.join(', ') : ''
      return renderLabelReadOnlyHeader(
        valueToDisplay,
        control,
        sectionData,
        rfiMasterData,
      )
    }

    default:
      break
  }
}
